import { AcceptInviteComponent } from './modules/onboarding/components/accept-invite/accept-invite.component';
import { AccountComponent } from './modules/account/components/account.component';
import { DashboardComponent } from './modules/dashboard/components/dashboard.component';
import { HomeDeviceDetailsComponent } from './modules/home-devices/components/home-device-details/home-device-details.component';
import { ClaimsSupportComponent } from './modules/claims-support/components/claims-support/claims-support.component';
import { ErrorComponent } from './core/components/error/error.component';
import { InviteExpiredComponent } from './modules/onboarding/components/invite-expired/invite-expired.component';
import { NgModule } from '@angular/core';
import { OktaCallbackComponent } from '@okta/okta-angular';
import { RouterModule, Routes } from '@angular/router';
import { AccountSetupComponent } from './modules/onboarding/components/account-setup.component';
import { SignupInviteComponent } from './modules/onboarding/components/signup-invite/signup-invite.component';
import { TechServicesComponent } from './modules/tech-services/components/tech-services.component';
import { LoginComponent } from './modules/auth/components/login/login.component';
import { LogoutComponent } from './modules/auth/components/logout/logout.component';
import { TechSupportHistoryComponent } from './modules/tech-support-history/components/tech-support-history.component';
import { HealthCheckPageComponent } from './modules/health-check/health-check-page/health-check-page.component';
import { SpeedTestComponent } from './modules/tech-services/components/speed-test/speed-test.component';
import { FamilyManagementComponent } from './modules/account/components/family-management/family-management.component';
import { BillingInfoAndHistoryComponent } from './modules/account/components/billing-info-and-history/billing-info-and-history.component';
import { ChatTranscriptComponent } from './modules/tech-support-history/components/chat-transcript/chat-transcript.component';
import { TechSupportHistoryListComponent } from './modules/tech-support-history/components/tech-support-history-list/tech-support-history-list.component';
import { ClaimsFlowComponent } from './modules/account/components/claims-flow/claims-flow.component';
import { AuthGuard } from './core/guards/auth.guard';
import { dashboardRoute } from './route-constants';
import { AccountDetailsComponent } from './modules/account/components/account-details/account-details.component';
import { AccountProfileComponent } from './modules/account/components/account-profile/account-profile.component';
import { PasswordSettingsComponent } from './shared/components/password-settings/password-settings.component';
import { OktaHostedLoginComponent } from './modules/auth/components/okta-hosted-login/okta-hosted-login.component';
import { SelectedDeviceGuard } from 'src/app/shared/device/selected-device.guard';
import { DeviceInventoryComponentV3 } from './modules/home-devices/components/device-inventory-v3/device-inventory-v3.component';
import { ClaimsSelectDeviceComponent } from './modules/account/components/claims-flow/claims-select-device/claims-select-device.component';
import { PhoneDiagnosticsComponent } from './modules/device-diagnostics/phone-diagnostics.component';
import { PhoneOnboardingComponent } from './modules/device-diagnostics/phone-onboarding/phone-onboarding.component';
import { PostDiagnosticLandingPageComponent } from './modules/device-diagnostics/components/post-diagnostic-landing-page/post-diagnostic-landing-page.component';
import { TestPageComponent } from './modules/poc/components/testpage.component';
import { SiteConfigResolver } from './core/resolvers/site-config.resolver';
import { TestPageComponent2 } from './modules/poc/components/testpage2/testpage2.component';
import { PageResourcesResolver } from './core/resolvers/page-resources.resolver';
import { CMSComponent } from './modules/cms/components/cms/cms.component';
import { EditorComponent } from './modules/cms/components/editor/editor.component';
import { FaqComponent } from './shared/components/faq/faq.component';
import { FaqP360Component } from './shared/components/faq/p360-faq/p360-faq.component';
import { CreateAccountComponent } from './modules/onboarding/components/create-account/create-account.component';
import { PhoneRegistrationComponent } from './modules/device-diagnostics/phone-registration/phone-registration.component';
import { TabletRegistrationComponent } from './modules/device-diagnostics/tablet-registration/tablet-registration.component';
import { TechSupportComponent } from './modules/tech-support/tech-support.component';
import { SmartwatchRegistrationComponent } from './modules/device-diagnostics/smartwatch-registration/smartwatch-registration.component';
import { CommunicationPreferencesComponent } from './modules/account/components/communication-preferences/communication-preferences.component';
import { AdditionalDeviceSelectionComponent } from './modules/device-diagnostics/components/additional-device-selection/additional-device-selection.component';
import { CreateAccountComponentV0 } from './modules/onboarding/components/create-account-v0/create-account.component';
import { DeductibleLookupComponent } from './modules/deductible-lookup/components/deductible-lookup.component';
import { DocumentComponent } from './modules/document/components/doument.component';
import { UnderMaintenanceComponent } from './modules/under-maintenance/under-maintenance.component';
import { SubscriptionComponent } from './modules/account/components/subscription/subscription.component';
import { CoverageComponent } from './modules/coverage/components/coverage.component';
import { CoverageMobileComponent } from './modules/coverage/components/coverage-mobile/coverage-mobile.component';
import { CoverageHomeComponent } from './modules/coverage/components/coverage-home/coverage-home.component';
import { GenericContentPageComponent } from './modules/generic-content-page/generic-content-page.component';

const routes: Routes = [
  {
    path: '',
    data: {
      loadGlobal: true,
    },
    resolve: { resources: SiteConfigResolver },
    children: [
      {
        path: '',
        data: {
          title: '',
        },
        resolve: { resources: PageResourcesResolver },
        runGuardsAndResolvers: 'always',
        children: [
          {
            path: '',
            redirectTo: `/${dashboardRoute}`,
            pathMatch: 'full',
          },
          {
            path: dashboardRoute,
            component: DashboardComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: 'DashboardPage',
              title: 'Dashboard',
              sfresources: 'Global,Modal,Chat,Dashboard,Promo,Tutorial,Perks,FAQ,Coverage',
            },
          },
          {
            path: 'account',
            component: AccountComponent,
            resolve: { resources: PageResourcesResolver },
            data: {},
            canActivate: [AuthGuard],
            children: [
              {
                path: '',
                redirectTo: 'details',
                pathMatch: 'full',
              },
              {
                path: 'details',
                component: AccountDetailsComponent,
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Account',
                  sfresources: 'Global,Modal,Chat,Account,Tutorial',
                },
              },
              {
                path: 'profile',
                component: AccountProfileComponent,
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Profile',
                  sfresources: 'Global,Modal,Chat,Account,Tutorial',
                },
              },
              {
                path: 'change-password',
                component: PasswordSettingsComponent,
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Change Password ',
                  sfresources: 'Global,Modal,Chat,Account,PasswordForm,Tutorial',
                },
              },
              {
                path: 'payment-info',
                component: BillingInfoAndHistoryComponent,
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Billing And Payment History',
                  sfresources: 'Global,Modal,Chat,Account,Tutorial',
                },
              },
              {
                path: 'family-management',
                component: FamilyManagementComponent,
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Family Management',
                  sfresources: 'Global,Modal,Chat,Account,Tutorial',
                },
              },
              {
                path: 'communication-preferences',
                component: CommunicationPreferencesComponent,
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Communication Preferences',
                  sfresources: 'Global,Modal,Chat,Account,Tutorial',
                },
              },
              {
                path: 'subscription',
                component: SubscriptionComponent,
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Subscription',
                  sfresources: 'Global,Modal,Chat,Account,Tutorial',
                },
              },
              {
                path: 'tech-support-history',
                component: TechSupportHistoryComponent,
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: 'TechSupportHistoryComponent',
                  title: 'Tech Support History',
                  sfresources: 'Global,Modal,Chat,Account,Tutorial',
                },
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    component: TechSupportHistoryListComponent,
                    resolve: { resources: PageResourcesResolver },
                    data: {
                      component: 'TechSupportHistoryListComponent',
                      title: '',
                      sfresources: 'Global,Modal,Chat,Account,Tutorial',
                    },
                  },
                  {
                    path: 'chat-transcript/:id',
                    component: ChatTranscriptComponent,
                    resolve: { resources: PageResourcesResolver },
                    data: {
                      component: 'ChatTranscriptComponent',
                      title: 'Chat Transcript Component',
                      sfresources: 'Global,Modal,Chat,Account,Tutorial',
                    },
                  },
                ],
              },
            ],
          },
          {
            path: 'payment-info',
            redirectTo: '/account/payment-info',
          },
          {
            path: 'tech-support-history',
            redirectTo: '/account/tech-support-history',
          },
          {
            path: 'account/claims',
            redirectTo: '/claims',
          },
          {
            path: 'file-a-claim',
            redirectTo: '/claims',
          },
          {
            path: 'tech-services',
            component: TechServicesComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Tech Services',
              sfresources: 'Global,Modal,Chat,TechServices,Tutorial',
            },
          },
          {
            path: 'coverage',
            component: CoverageComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Coverage',
              sfresources: 'Global,Modal,Chat,Coverage,Tutorial',
            },
          },
          {
            path: 'coverage-mobile',
            component: CoverageMobileComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Coverage - Protection 360™',
              sfresources: 'Global,Modal,Chat,Coverage,Tutorial',
            },
          },
          {
            path: 'coverage-home',
            component: CoverageHomeComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Coverage - Protection 360™ HomeTech',
              sfresources: 'Global,Modal,Chat,Coverage,Tutorial,Dashboard',
            },
          },
          {
            path: 'claims-support',
            component: ClaimsSupportComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Support Center',
              sfresources: 'Global,Modal,Chat,Claims,ClaimsSupportCenter,FAQ,Tutorial',
            },
          },
          {
            path: 'my-devices',
            component: DeviceInventoryComponentV3,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'My Devices',
              sfresources: 'Global,Modal,Chat,Devices,DeviceInventory,DeviceSearch,DeviceCard,DeviceDetails,RegisterTablet,RegisterWatch,RegisterSmartwatch,RegisterCellularDevice,RegisterNonCellularDevice,Tutorial',
            },
          },
          {
            path: 'home-device-details',
            component: HomeDeviceDetailsComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard, SelectedDeviceGuard],
            data: {
              component: '',
              title: 'Home Device Details',
              sfresources: 'Global,Modal,Chat,DeviceDetails,Devices,RegisterTablet,RegisterWatch,RegisterSmartwatch,RegisterCellularDevice,RegisterNonCellularDevice,CBR,Tutorial',
            },
          },
          {
            // TODO: RC: re-evaluate the path structure for the device content and probably redo it once all the other pieces of the device-inventory is there.
            path: 'device-content',
            canActivate: [AuthGuard],
            data: {
              title: '',
            },
            loadChildren: () => import('./modules/device-content/device-content.module').then((m) => m.DeviceContentModule),
          },
          {
            path: 'device-select',
            component: ClaimsSelectDeviceComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Claims Device Select',
              sfresources: 'Global,Modal,Chat,ClaimsSelectDevice,DeviceInventory,DeviceCard,Tutorial',
            },
          },
          {
            path: 'claims',
            component: ClaimsFlowComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: 'FileAClaimPage',
              title: 'File a Claim',
              sfresources: 'Global,Modal,Chat,Claims,Tutorial',
            },
          },
          {
            path: 'speed-check',
            component: SpeedTestComponent,
            resolve: { resources: PageResourcesResolver },
            data: {
              component: '',
              title: 'Speedcheck',
              sfresources: 'Global,Modal,Chat,SpeedTest,Tutorial',
            },
          },
          {
            path: 'faq',
            component: FaqComponent,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'FAQ',
              sfresources: 'Global,Modal,Chat,FAQ,Tutorial',
            },
          },
          {
            path: 'p360-faq',
            component: FaqP360Component,
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'FAQ P360',
              sfresources: 'Global,Modal,Chat,FAQ,Tutorial',
            },
          },
          {
            path: 'live-help',
            loadComponent: () => import('./modules/live-help/live-help.component').then((m) => m.LiveHelpComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Live Help',
              sfresources: 'Global,Modal,Chat,LiveHelp',
            },
          },
        ],
      },
      {
        path: 'login',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: '',
          sfresources: 'Global,Modal,Chat,Home,Login,SignUp',
        },
        children: [
          {
            path: '',
            component: LoginComponent,
            data: {
              title: 'Login',
            },
          },
          {
            path: 'callback',
            component: OktaCallbackComponent,
            data: {
              title: '',
            },
          },
        ],
      },
      {
        path: 'sso',
        component: OktaHostedLoginComponent,
        data: {
          title: 'Login SSO',
        },
      },
      {
        path: 'logout',
        component: LogoutComponent,
        canActivate: [AuthGuard],
        data: {
          title: 'Logout',
        },
      },
      {
        path: 'signin/forgot-password',
        component: LoginComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Forgot Password',
          sfresources: 'Global,Modal,Chat,Home,Login,SignUp',
        },
      },
      {
        path: 'signup',
        component: AccountSetupComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Onboarding',
          sfresources: 'Global,Modal,Chat,Signup,PasswordForm,SPOE,Tutorial',
        },
      },
      {
        path: 'create-account',
        component: CreateAccountComponentV0,
        resolve: { resources: PageResourcesResolver },
        data: {
          title: 'Onboarding',
          sfresources: 'Global,Modal,Chat,Signup,PasswordForm,LookupUser,URL,SPOE,Tutorial',
        },
      },
      {
        path: 'register-account',
        component: CreateAccountComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          title: 'Onboarding',
          sfresources: 'Global,Modal,Chat,Signup,PasswordForm,LookupUser,URL,SPOE,Tutorial',
        },
      },
      {
        path: 'invite-expired',
        component: InviteExpiredComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Onboarding Invite Expired',
          sfresources: 'Global,Modal,Chat,SignUp,Tutorial',
        },
      },
      {
        path: 'accept-invite',
        component: AcceptInviteComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Onboarding Accept Invite',
          sfresources: 'Global,Modal,Chat,SignUp,Tutorial',
        },
      },
      {
        path: 'signup-invite',
        component: SignupInviteComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Onboarding Signup Invite',
          sfresources: 'Global,Modal,Chat,SignUp,PasswordForm,Tutorial,LookupUser',
        },
      },
      {
        path: 'dr/:id',
        component: PhoneDiagnosticsComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Phone Diagnostics',
          sfresources: 'Global,Modal,IMEI_Info,Onboarding,RegisterCellularDevice,RegisterNumber,RegisterDiagnosticDevice,RegisterNonCellularDevice,Tutorial',
        },
      },
      {
        path: 'po',
        component: PhoneOnboardingComponent,
        resolve: { resources: PageResourcesResolver },
        canActivate: [AuthGuard],
        data: {
          component: '',
          title: 'Phone Onboarding',
          sfresources: 'Global,Modal,IMEI_Info,Onboarding,RegisterCellularDevice,RegisterNumber,RegisterDiagnosticDevice,RegisterNonCellularDevice,Tutorial',
        },
      },
      {
        path: 'additional-device-selection',
        component: AdditionalDeviceSelectionComponent,
        resolve: { resources: PageResourcesResolver },
        canActivate: [AuthGuard],
        data: {
          component: '',
          title: 'Register Additional Device',
          sfresources: 'Global,Modal,Chat,Devices,RegisterCellularDevice,RegisterNumber,Onboarding,RegisterDiagnosticDevice,Tutorial',
        },
      },
      {
        path: 'phone-registration',
        component: PhoneRegistrationComponent,
        resolve: { resources: PageResourcesResolver },
        canActivate: [AuthGuard],
        data: {
          component: '',
          title: 'Register Phone Number',
          sfresources: 'Global,Modal,Chat,Devices,RegisterCellularDevice,RegisterNumber,Onboarding,RegisterDiagnosticDevice,RegisterNonCellularDevice,Tutorial',
        },
      },
      {
        path: 'tablet-registration',
        component: TabletRegistrationComponent,
        resolve: { resources: PageResourcesResolver },
        canActivate: [AuthGuard],
        data: {
          component: '',
          title: 'Register Tablet',
          sfresources: 'Global,Modal,Chat,Devices,RegisterCellularDevice,RegisterNumber,RegisterTablet,IMEI_Info,Onboarding,RegisterDiagnosticDevice,RegisterNonCellularDevice,Tutorial',
        },
      },
      {
        path: 'smartwatch-registration',
        component: SmartwatchRegistrationComponent,
        resolve: { resources: PageResourcesResolver },
        canActivate: [AuthGuard],
        data: {
          component: '',
          title: 'Register Smartwatch',
          sfresources: 'Global,Modal,Chat,Devices,RegisterCellularDevice,RegisterNumber,RegisterTablet,RegisterWatch,RegisterSmartwatch,IMEI_Info,Onboarding,RegisterDiagnosticDevice,RegisterNonCellularDevice,Tutorial',
        },
      },
      {
        path: 'diagnostic-complete',
        component: PostDiagnosticLandingPageComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Phone Diagnostics Complete',
          sfresources: 'Global,PostDiagnostic,Tutorial',
        },
      },
      {
        path: 'tech-support',
        component: TechSupportComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Tech Support',
          sfresources: 'Global,Modal,Chat,Devices,TechSupport,Tutorial',
        },
      },
        {
        path: 'page/:key',
        component: GenericContentPageComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: '',
          sfresources: 'Global,Modal,Chat,GenericContent',
        },
      },
      {
        path: 'under-maintenance',
        component: UnderMaintenanceComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Under Maintenance',
          sfresources: 'Global,Modal,Chat,Devices,UnderMaintenance,TechSupport,Tutorial',
        },
      },
      {
        path: 'error',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Error',
          sfresources: 'Global,Modal,Chat,Error,Tutorial',
        },
        children: [
          {
            path: '',
            component: ErrorComponent,
          },
          {
            path: ':id',
            component: ErrorComponent,
          },
        ],
      },
      {
        path: 'test-page',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Test Page',
          sfresources: 'Global,POC,Tutorial',
        },
        children: [
          {
            path: '',
            component: TestPageComponent,
          },
        ],
      },
      {
        path: 'service_fee_deductibles',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Service Fee / Deductibles',
          sfresources: 'Global,Deductible,Tutorial',
        },
        children: [
          {
            path: '',
            component: DeductibleLookupComponent,
          },
        ],
      },
      {
        path: 'cms',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'CMS Page',
          sfresources: '_',
        },
        children: [
          {
            path: '',
            component: CMSComponent,
          },
        ],
      },
      {
        path: 'document/:filename',
        component: DocumentComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Document',
          sfresources: 'Global,Document',
        },
      },
      {
        path: 'documents/:filename',
        component: DocumentComponent,
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Document',
          sfresources: 'Global,Document',
        },
      },
      {
        path: 'editor',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Editor',
          sfresources: 'Global,Modal,Editor,Tutorial',
        },
        children: [
          {
            path: '',
            component: EditorComponent,
          },
        ],
      },
      {
        path: 'test-page2',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Test Page 2',
          sfresources: 'Global,POC,TestPage2',
        },
        children: [
          {
            path: '',
            component: TestPageComponent2,
          },
        ],
      },
    ],
  },
  {
    path: 'health-check',
    component: HealthCheckPageComponent,
  },
  {
    path: '**',
    redirectTo: '/error',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    scrollPositionRestoration: 'enabled',
    anchorScrolling: 'enabled',
    scrollOffset: [0, 0],
  })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
